import dayjs, { Dayjs } from "dayjs";
import { formatNumber } from "../../utils";
import { formatDate } from "../../utils/date";
import { ExpressDataColumn, ExpressDataRow, ExpressDataSummary } from "../../types/express-data.type";

export const EXPRESS_DATA_COLUMNS: ExpressDataColumn[] = [
    {
        key: "transaction_type",
        label: "ประเภทรายการ",
        minWidth: 130,
        type: "transaction_type",
    },
    { key: "transaction_no", label: "เลขที่รายการ", minWidth: 150 },
    {
        key: "transaction_date",
        label: "วันที่รายการ",
        align: "center",
        minWidth: 120,
        type: "date",
    },
    { key: "route_code", label: "รหัสสายส่ง", minWidth: 120 },
    { key: "company_branchcode", label: "รหัสสาขา", minWidth: 110 },
    { key: "company_branchname", label: "ชื่อสาขา", minWidth: 170 },
    { key: "warehouse", label: "คลังสินค้า", minWidth: 110 },
    { key: "customer_code", label: "รหัสลูกค้า", minWidth: 120 },
    { key: "customer_name", label: "ชื่อลูกค้า", minWidth: 180 },
    { key: "emp_code", label: "รหัสพนักงาน", minWidth: 120 },
    { key: "emp_name", label: "ชื่อพนักงาน", minWidth: 160 },
    { key: "po_number", label: "เลขที่ PO", minWidth: 130 },
    {
        key: "total_amount",
        label: "ยอดรวม",
        align: "right",
        minWidth: 110,
        type: "number",
    },
    {
        key: "discount_amount",
        label: "ส่วนลด",
        align: "right",
        minWidth: 110,
        type: "number",
    },
    {
        key: "after_discount",
        label: "ยอดหลังหักส่วนลด",
        align: "right",
        minWidth: 160,
        type: "number",
    },
    { key: "tax_type", label: "ประเภทภาษี", minWidth: 120 },
    {
        key: "tax_amount",
        label: "ภาษี",
        align: "right",
        minWidth: 110,
        type: "number",
    },
    {
        key: "grand_total",
        label: "ยอดสุทธิ",
        align: "right",
        minWidth: 120,
        type: "number",
    },
    {
        key: "status",
        label: "สถานะ",
        align: "center",
        minWidth: 130,
        type: "status",
    },
    { key: "payment_type", label: "วิธีชำระเงิน", minWidth: 130 },
];

export const EXPRESS_DATA_TRANSACTION_TYPE_OPTIONS = [
    { value: "invoice", label: "ใบแจ้งหนี้" },
    { value: "cashsale", label: "ขายสด" },
] as const;

export const EXPRESS_DATA_STATUS_OPTIONS = [
    { value: "pending", label: "รอดำเนินการ" },
    { value: "paid", label: "ชำระแล้ว" },
    { value: "approve", label: "อนุมัติแล้ว" },
    { value: "cancel", label: "ยกเลิก" },
    { value: "pending_cancel", label: "รอยกเลิก" },
] as const;

export const getDefaultExpressDate = (): Dayjs => dayjs();

export const getExpressDataTransactionDateParam = (date: Dayjs) => date.format("YYYY-MM-DD");

export const getExpressDataSummary = (count: number, totalNetValue: number): ExpressDataSummary => ({
    totalTransactions: count,
    totalNetValue,
});

const getTransactionTypeLabel = (value: string | null) =>
    EXPRESS_DATA_TRANSACTION_TYPE_OPTIONS.find((item) => item.value === value)?.label ?? value ?? "-";

const getStatusLabel = (value: string | null) =>
    EXPRESS_DATA_STATUS_OPTIONS.find((item) => item.value === value)?.label ?? value ?? "-";

export const formatExpressCellValue = (item: ExpressDataRow, column: ExpressDataColumn) => {
    const value = item[column.key];

    if (value === null || value === undefined || value === "") return "-";
    if (column.type === "date") return formatDate(String(value));
    if (column.type === "number") {
        return formatNumber(value as number, { decimals: 2 });
    }
    if (column.type === "transaction_type") {
        return getTransactionTypeLabel(String(value));
    }
    if (column.type === "status") return getStatusLabel(String(value));

    return String(value);
};

export const getFilenameFromContentDisposition = (header?: string) => {
    if (!header) return "";

    const utf8Match = header.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) return decodeURIComponent(utf8Match[1]);

    const filenameMatch = header.match(/filename="?([^"]+)"?/i);
    return filenameMatch?.[1] ?? "";
};

export const getExpressDataCsvFilename = () => `express_data_${dayjs().format("YYYYMMDD_HHmmss")}.csv`;
